import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Button, Card, CardHeader, CardMedia, CircularProgress, Container, Grid, MenuItem, Pagination, SelectChangeEvent } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as EngineHelper from '../api/engine/engineHelper';
import { IPaginatedResponse } from '../api/httpClient';
import * as ProjectHelper from '../api/project/projectHelper';
import { IGetVesselPreviewResponse } from '../api/scenario/iApiResponse';
import * as ScenarioHelper from '../api/scenario/scenarioHelper';
import { route } from '../route/Index';
import DropDown from '../shared/component/DropDown';
import { ETypeOfPropeller } from '../shared/enum';
import isFulfilledProject from '../shared/hoc/isFulfilledProject';
import { IEngine, IProjectInfo } from '../shared/iProject';
import variable from '../shared/style/variable';
import { useAppDispatch, useAppSelector } from '../store/hook';
import { selectProject, setProject, setProjectInfo } from '../store/project/projectSlice';
import { selectUser } from '../store/user/userSlice';
import { cleanResult } from '../store/result/resultSlice';
import { cleanEngine } from '../store/engine/engineSlice';
import { cleanMap } from '../store/map/mapSlice';
import { cleanAlert } from '../store/alert/alertSlice';

interface IScenarioProps {}

const ScenarioContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
    margin-top: 1rem;
`;

let isAlreadyBowThruster = false;
let engineSelected: IEngine[] = [];

const Scenario = (props: IScenarioProps) => {
    let pageSize = 4;

    const dispatch = useAppDispatch();
    const project = useAppSelector(selectProject);
    const user = useAppSelector(selectUser);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [vesselPreview, setVesselPreview] = React.useState<IPaginatedResponse<IGetVesselPreviewResponse>>();
    const [engine, setEngine] = React.useState<IPaginatedResponse<IEngine>>();
    const [vessel, setVessel] = React.useState<number | undefined>();
    const [isLoading, setIsLoading] = React.useState(false);
    const [pageNumber, setpageNumber] = React.useState(1);

    React.useEffect(() => {
        const fetchVessel = async () => {
            setIsLoading(true);
            let res = await ScenarioHelper.getScenarioPreviewAsync(undefined, undefined, undefined, user.data!.isAdmin);
            setVesselPreview(res.data);
            setVessel(res.data.value[0].id);
            dispatch(setProjectInfo({ scenarioId: res.data.value[0].id }));
            setIsLoading(false);
        };
        fetchVessel();
    }, []);

    React.useEffect(() => {
        if (!!vessel) {
            const fetchEnginePreview = async (page: number) => {
                setIsLoading(true);
                let res = await EngineHelper.getEnginesByVesselAsync(vessel, page, pageSize, undefined, user.data!.isAdmin);
                setEngine(res.data);
                engineSelected = res.data.value.filter(x =>
                    engineSelected.some(
                        y =>
                            x.typeOfPropeller == y.typeOfPropeller &&
                            x.propellerDirectionWithRespectToTheQuay == y.propellerDirectionWithRespectToTheQuay &&
                            x.numberOfPropellers == y.numberOfPropellers,
                    ),
                );
                engineSelected = engineSelected.length > 0 ? engineSelected : [res.data.value[0]];
                dispatch(setProjectInfo({ engineIds: engineSelected.map(x => x.id!) }));
                setIsLoading(false);
            };
            fetchEnginePreview(pageNumber);
        }
    }, [vessel, pageNumber]);

    const handleChangeVesselType = (event: SelectChangeEvent<any>) => {
        setVessel(parseInt(event.target.value));
        dispatch(setProjectInfo({ scenarioId: parseInt(event.target.value) }));
    };

    const handleChangeEngineId = (obj: IEngine) => {
        let engineIds = [...project.data?.project!.engineIds!];
        let selectedEngine = engine?.value.find(x => x.id === obj.id!);
        if (engineIds.includes(obj.id!)) {
            if (selectedEngine?.typeOfPropeller == ETypeOfPropeller.BowThruster) isAlreadyBowThruster = false;
            engineSelected = engineSelected.filter(x => x.id !== obj.id);
            dispatch(setProjectInfo({ engineIds: engineSelected.map(x => x.id!) }));
        } else {
            if (selectedEngine?.typeOfPropeller == ETypeOfPropeller.BowThruster && isAlreadyBowThruster) {
                Swal.fire({
                    title: t('combinationNotAllowed'),
                    icon: 'warning',
                    toast: true,
                    position: 'top',
                    showConfirmButton: false,
                    timer: 2000,
                });
            } else {
                if (selectedEngine?.typeOfPropeller == ETypeOfPropeller.BowThruster) isAlreadyBowThruster = true;
                engineSelected = [...engineSelected, obj];
                dispatch(setProjectInfo({ engineIds: [...project.data?.project!.engineIds!, obj.id!] }));
            }
        }
    };

    const handleCreateProject = async () => {
        setIsLoading(true);
        if (project.data?.project?.id) {
            Swal.fire({
                title: t('areYouSure'),
                text: t('warningReInizializeProject'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: variable.green,
                cancelButtonColor: variable.red,
                confirmButtonText: t('yes'),
                cancelButtonText: t('cancel'),
            }).then(async result => {
                if (result.isConfirmed) {
                    let payload: IProjectInfo = {
                        ...project.data!.project,
                        authorEmail: user.data?.email,
                        authorName: user.data?.first_Name,
                        authorSurname: user.data?.last_Name,
                        geometryId: project.data?.geometry?.baseGeometryId,
                    };
                    let res = await ProjectHelper.createProjectAsync(payload);
                    if (res.status == 200) {
                        dispatch(cleanResult());
                        dispatch(cleanEngine());
                        dispatch(cleanMap());
                        dispatch(cleanAlert());
                        dispatch(setProject(res.data));
                        navigate(route.design.replace(':projectId', res.data.project!.id!.toString()));
                    }
                }
            });
        } else {
            let res = await ProjectHelper.createProjectAsync(project.data!.project!);
            if (res.status == 200) {
                dispatch(setProject(res.data));
                navigate(route.design.replace(':projectId', res.data.project!.id!.toString()));
            }
        }
        setIsLoading(false);
    };

    return (
        <ScenarioContainer maxWidth='md'>
            <Stack height={'100%'} direction='column' spacing={4}>
                <Typography variant='h1'>{t('scenarioCreation')}</Typography>
                {isLoading && <CircularProgress />}
                {!isLoading && vesselPreview?.value?.length! > 0 && engine?.value?.length! > 0 && (
                    <>
                        <Stack flexGrow={1} direction='column' marginTop={'1rem!important'} spacing={1}>
                            <DropDown
                                textTooltip={t('vesselTypeDescription')}
                                inputLabel={t('vesselType')}
                                labelId='vessel-type-label'
                                value={vessel}
                                defaultValue={vesselPreview?.value[0].id.toString()}
                                id='vessel-type'
                                onChange={handleChangeVesselType}>
                                {vesselPreview!.value.map(x => (
                                    <MenuItem key={x.id} value={x.id}>
                                        {x.vesselType}
                                    </MenuItem>
                                ))}
                            </DropDown>
                            <Stack flexGrow={1} direction={'column'} justifyContent='space-between'>
                                <Grid container spacing={5}>
                                    {engine!.value.map(x => (
                                        <Grid key={x.id} item md={3} sm={6} xs={12}>
                                            <Card
                                                style={{ height: '220px', width: '180px', margin: 'auto' }}
                                                variant={project.data?.project?.engineIds?.includes(x.id!) ? 'selected' : 'standard'}
                                                onClick={() => handleChangeEngineId(x)}>
                                                <Stack height={'30%'} direction={'row'} padding={'0rem 1rem'} justifyContent={'space-between'} alignItems={'center'}>
                                                    <CardHeader style={{ padding: 0 }} title={<Typography>{x.translations![0].name}</Typography>} />
                                                    {project.data?.project?.engineIds?.includes(x.id!) ? (
                                                        <CheckBoxIcon style={{ fill: variable.green }} />
                                                    ) : (
                                                        <CheckBoxOutlineBlankIcon style={{ fill: variable.green }} />
                                                    )}
                                                </Stack>
                                                <CardMedia height={'70%'} component='img' image={x.image?.imageUrl} alt={''} />
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                                {engine!.pageCount > 1 && (
                                    <Stack alignItems='end'>
                                        <Pagination showFirstButton showLastButton page={engine!.pageNumber} count={engine!.pageCount} onChange={(e, value) => setpageNumber(value)} />
                                    </Stack>
                                )}
                            </Stack>
                        </Stack>
                        <Stack alignItems='end'>
                            <Button disabled={project.data?.project!.engineIds?.length == 0} variant='orange' onClick={handleCreateProject}>
                                {t('next')}
                            </Button>
                        </Stack>
                    </>
                )}
            </Stack>
        </ScenarioContainer>
    );
};

export default isFulfilledProject(Scenario);
